import { CirclePlus, Layers, Loader, Pause, Play, Square, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ScrollableCellWithColorDot } from '@/components/data-table/cells/scrollable-cell';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { formatHoursToTimeString } from '@/helpers';
import { normalizeColorHex } from '@/lib/utils';

import { getProgressColor } from '../helpers/progress-color.helper';
import {
  ProjectTimerControls,
  ProjectTimerState,
  ProjectTimeTrackingSummary,
} from '../types/time-tracking';

interface ProjectHeaderProps {
  project: ProjectTimeTrackingSummary;
  onAdd: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isCreateWorklogActionVisible: boolean;
  timerControls: ProjectTimerControls;
}

export function ProjectHeader({
  project,
  onAdd,
  isCreateWorklogActionVisible,
  timerControls,
}: ProjectHeaderProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const progressColor = getProgressColor(project.progressPercentage);
  const isAllBackgroundWork =
    project.bookedBreakdown.length > 0 && project.bookedBreakdown.every(b => b.isBackgroundWork);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-6 p-4 pl-0">
      <div className="min-w-0 flex-1">
        <ScrollableCellWithColorDot
          text={project.project.projectName}
          image={project.project.logoUrl}
          avatarName={project.project.projectName}
          avatarSize="sm"
          alt={`${project.project.projectName} logo`}
          dotColor={normalizeColorHex(project.project.colorHex)}
          className="w-full"
          textClassName="text-sm font-medium"
        />
      </div>

      {isAllBackgroundWork && (
        <SimpleTooltip content={t('weekView.cell.backgroundWorkTooltip')}>
          <span
            aria-label={t('weekView.cell.backgroundWorkBadgeAriaLabel')}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-foreground/10 text-foreground/70"
          >
            <Layers className="h-3.5 w-3.5" />
          </span>
        </SimpleTooltip>
      )}

      <div className="flex shrink-0 justify-end">
        <div className="flex items-center gap-4">
          <div className="flex w-[220px] flex-col gap-1">
            <div className="flex items-baseline justify-between text-[11px] tabular-nums text-muted-foreground">
              <span>
                {t('project.trackedLabel', {
                  tracked: formatHoursToTimeString(project.trackedHours),
                })}
              </span>
              <span>
                {project.bookedHours > 0
                  ? t('project.bookedLabel', {
                      booked: formatHoursToTimeString(project.bookedHours),
                    })
                  : t('project.notBooked')}
              </span>
            </div>
            <Progress
              value={Math.min(project.progressPercentage, 100)}
              indicatorColor={progressColor}
              className="h-2 w-full"
            />
          </div>
          <div className="w-[42px] text-sm text-muted-foreground">
            {t('project.progressPercentage', {
              percentage: project.progressPercentage.toFixed(0),
            })}
          </div>
        </div>
      </div>

      {isCreateWorklogActionVisible && (
        <div className="flex shrink-0 items-center justify-end gap-0">
          <TimerControls timerControls={timerControls} />
          <Button variant="ghost" size="icon" onClick={onAdd}>
            <CirclePlus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function TimerControls({ timerControls }: { timerControls: ProjectTimerControls }) {
  const { t } = useTranslation('authenticated.timeTracking');

  if (timerControls.state === ProjectTimerState.ActiveHere) {
    return (
      <>
        {!timerControls.isLimitReached && (
          <SimpleTooltip
            content={timerControls.isPaused ? t('tracker.resume') : t('tracker.pause')}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={timerControls.onTogglePause}
              disabled={timerControls.isAnyActionPending}
            >
              {timerControls.isTogglePausePending ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : timerControls.isPaused ? (
                <Play className="h-4 w-4" />
              ) : (
                <Pause className="h-4 w-4" />
              )}
            </Button>
          </SimpleTooltip>
        )}
        <SimpleTooltip content={t('tracker.stop')}>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={timerControls.onStop}
            disabled={timerControls.isAnyActionPending}
          >
            {timerControls.isStopPending ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Square className="h-4 w-4" fill="currentColor" />
            )}
          </Button>
        </SimpleTooltip>
        <SimpleTooltip content={t('tracker.reset')}>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            onClick={timerControls.onReset}
            disabled={timerControls.isAnyActionPending}
          >
            {timerControls.isDiscardPending ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        </SimpleTooltip>
      </>
    );
  }

  if (timerControls.state === ProjectTimerState.ActiveElsewhere) {
    return (
      <SimpleTooltip content={t('project.playButtonDisabledTooltip')}>
        <span className="inline-flex">
          <Button variant="ghost" size="icon" disabled>
            <Play className="h-4 w-4 text-muted-foreground/75" />
          </Button>
        </span>
      </SimpleTooltip>
    );
  }

  if (timerControls.state === ProjectTimerState.Idle) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={timerControls.onPlay}
        disabled={timerControls.isAnyActionPending}
      >
        {timerControls.isStartPending ? (
          <Loader className="h-4 w-4 animate-spin" />
        ) : (
          <Play className="h-4 w-4" />
        )}
      </Button>
    );
  }

  return null;
}
